import { useMemo, useState } from 'react'
import {Copy, Check, MessageCircleDashed as MessageCircle, ExternalLink} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { copiarTexto } from '@/utils/clipboard'

interface IndicacaoCapacitacaoProps {
  /** Slug/usuario da consultora (ex: "monica") — usado no parametro ?indicado_por= */
  usuarioConsultora: string
  /** Nome de exibicao da consultora — usado em saudacoes/copys. */
  nomeConsultora: string
}



function CopiarBotao({ texto, label, full }: { texto: string; label: string; full?: boolean }) {
  const [ok, setOk] = useState(false)
  return (
    <button
      onClick={async () => {
        try {
          await copiarTexto(texto)
          setOk(true)
          toast.success('Copiado!')
          setTimeout(() => setOk(false), 1800)
        } catch {
          toast.error('Erro ao copiar')
        }
      }}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all min-h-[44px] ${
        ok ? 'bg-verde-muito-claro text-verde-escuro border-2 border-verde-escuro' : 'bg-verde-escuro text-white hover:bg-opacity-90'
      } ${full ? 'w-full' : ''}`}
    >
      {ok ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {ok ? 'Copiado!' : label}
    </button>
  )
}

export default function IndicacaoCapacitacao({ usuarioConsultora, nomeConsultora }: IndicacaoCapacitacaoProps) {
  const linkIndicacao = useMemo(
    () => `https://www.descubraseusoleos.com.br/capacitacao?indicado_por=${encodeURIComponent(usuarioConsultora)}`,
    [usuarioConsultora]
  )

  const mensagemSugerida = `Oi [nome]! 🌿

Você quer trabalhar com bem-estar, saúde física e emocional e ter uma renda crescente e escalável?

Estou com uma seleção aberta para pessoas que querem atuar nessa área com método e estratégia.

Quem for selecionado(a) recebe:

✅ Capacitação completa em óleos essenciais com certificado totalmente gratuito

✅ Conhecimento para dar os primeiros passos no mercado de bem-estar e aromaterapia

✅ Orientação personalizada sobre como transformar isso em uma renda real e crescente

As vagas são limitadas.

Clica no link abaixo e faz sua inscrição:
${linkIndicacao}`

  return (
    <div className="space-y-6">
      <p className="text-sm text-texto-suave leading-relaxed">
        Indique pessoas que você conhece para participar da seleção e capacitação gratuita na área de bem-estar, saúde física e emocional. Acompanhe quem você indicou e em qual etapa cada pessoa está.
      </p>

      {/* Link de indicação */}
      <div>
        <div className="bg-[#D8F3DC] border-2 border-verde-escuro rounded-2xl px-5 py-4 mb-3">
          <div className="text-xs text-verde-escuro font-medium uppercase tracking-wide mb-1">
            Seu link de indicação
          </div>
          <div className="text-verde-escuro font-medium text-sm sm:text-base break-all">
            {linkIndicacao}
          </div>
        </div>
        <CopiarBotao texto={linkIndicacao} label="Copiar link de indicação" full />
      </div>

      {/* Mensagem sugerida */}
      <div>
        <p className="text-sm font-medium text-texto-escuro mb-2">
          Mensagem sugerida (com link):
        </p>
        <div
          className="bg-bege-fundo border border-bege-medio rounded-xl px-4 py-4 text-sm text-texto-escuro leading-relaxed whitespace-pre-wrap mb-3"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          {mensagemSugerida}
        </div>
        <CopiarBotao texto={mensagemSugerida} label="Copiar mensagem com link" full />
      </div>

      {/* Botões de ação */}
      <div className="flex flex-wrap gap-3">
        <a
          href={linkIndicacao}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-white text-verde-escuro border-2 border-verde-escuro hover:bg-verde-muito-claro transition-all min-h-[44px]"
        >
          <ExternalLink className="w-4 h-4" />
          Abrir página da capacitação
        </a>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(mensagemSugerida)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-[#25D366] text-white hover:bg-opacity-90 transition-all min-h-[44px]"
        >
          <MessageCircle className="w-4 h-4" />
          Compartilhar no WhatsApp
        </a>
      </div>


    </div>
  )
}

